import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Phone } from "lucide-react";
import { fmtDateTime } from "@/lib/dateFormat";
import { useAuth } from "@/contexts/AuthContext";
import { setNavGuard } from "@/lib/navGuard";

/* LEAD DESK BUILD item 2 — ONE form and ONE button. The notes are compulsory and they are what
   the call record carries, so nothing typed here is ever thrown away: the old "Set next call"
   button saved a date and dropped the note, which is how nine follow-ups ended up blank.
   FIX-UP (Fix 6): the typed note is kept as a per-lead draft in localStorage, and in-app
   navigation away from it asks first. */
export const LeadFollowUpCard = ({ leadId, canEdit, onChanged }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [outcome, setOutcome] = useState("");
  const [notes, setNotes] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [lostReason, setLostReason] = useState("");
  const [lostOther, setLostOther] = useState("");
  const [busy, setBusy] = useState(false);
  const [leaveAsk, setLeaveAsk] = useState(null);   // Fix 6a — the pending navigation
  const restored = useRef(false);

  const draftKey = `ld_call_draft:${user?.id || user?._id || "me"}:${leadId}`;

  const load = useCallback(() => {
    api.get(`/leads/${leadId}/follow-up`).then(r => setData(r.data)).catch(() => {});
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  // Fix 6(b) — bring back the unfinished draft when the rep returns to this lead
  useEffect(() => {
    if (restored.current) return;
    restored.current = true;
    try {
      const raw = window.localStorage.getItem(draftKey);
      if (raw) {
        const d = JSON.parse(raw);
        if (d.notes) setNotes(d.notes);
        if (d.outcome) setOutcome(d.outcome);
        if (d.dueAt) setDueAt(d.dueAt);
        if (d.notes || d.outcome || d.dueAt) toast.info("Your unsaved call note was restored");
      }
    } catch { /* a broken localStorage never blocks the form */ }
  }, [draftKey]);

  const closing = (data?.closing_outcomes || []).includes(outcome);
  const dirty = notes.trim().length > 0 || outcome !== "" || dueAt !== "";

  // Fix 6(b) — every keystroke is parked as the draft; a clean form has no draft
  useEffect(() => {
    if (!restored.current) return;
    try {
      if (dirty) window.localStorage.setItem(draftKey, JSON.stringify({ notes, outcome, dueAt }));
      else window.localStorage.removeItem(draftKey);
    } catch { /* ignore */ }
  }, [notes, outcome, dueAt, dirty, draftKey]);

  // Fix 6(a) — the browser still asks on refresh / tab close…
  useEffect(() => {
    const warn = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; return ""; } };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  // …and now in-app navigation asks too: sidebar links, another lead, any internal <a>
  useEffect(() => {
    if (!dirty) { setNavGuard(false); return; }
    setNavGuard(true, (proceed) => setLeaveAsk(() => proceed));
    const onClick = (e) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = e.target.closest && e.target.closest('a[href^="/"]');
      if (!a) return;
      e.preventDefault();
      e.stopPropagation();
      const href = a.getAttribute("href");
      setLeaveAsk(() => () => { setNavGuard(false); navigate(href); });
    };
    document.addEventListener("click", onClick, true);
    return () => { document.removeEventListener("click", onClick, true); setNavGuard(false); };
  }, [dirty, navigate]);

  const saveCall = async () => {
    if (!outcome) { toast.error("Pick what happened on the call"); return; }
    if (notes.trim().length < 3) { toast.error("Write what was said on the call (at least 3 characters)"); return; }
    if (!closing && !dueAt) { toast.error("Pick the date and time of the next call"); return; }
    const reason = lostReason === "Other" ? lostOther.trim() : lostReason;
    if (closing && !reason) { toast.error("Pick why this lead is lost"); return; }
    setBusy(true);
    try {
      const { data: resp } = await api.post(`/leads/${leadId}/log-call`, {
        outcome, notes: notes.trim(),
        next_call_at: closing ? "" : new Date(dueAt).toISOString(),
        lost_reason: reason,
      });
      setData(resp);
      setOutcome(""); setNotes(""); setDueAt(""); setLostReason(""); setLostOther("");
      try { window.localStorage.removeItem(draftKey); } catch { /* ignore */ }
      toast.success(closing ? "Call saved — the lead is closed as lost" : "Call saved");
      onChanged && onChanged();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save the call");
    } finally { setBusy(false); }
  };

  if (!data) return <div className="border border-zinc-200 rounded-sm p-4 text-xs text-zinc-400">Loading follow-up…</div>;
  const calls = data.calls || [];

  return (
    <div className="border border-zinc-200 rounded-sm p-4 bg-white" data-testid="lead-followup-card">
      {/* item 2(f) — internal notes stay their own field, at the top */}
      {data.internal_notes && (
        <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-sm" data-testid="lead-internal-notes">
          <p className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">Internal notes</p>
          <p className="text-xs text-amber-900 whitespace-pre-wrap">{data.internal_notes}</p>
        </div>
      )}

      <div className="flex items-center justify-between mb-2">
        <p className="text-sm font-semibold text-zinc-900 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Calls &amp; follow-up</p>
        <p className="text-[11px] text-zinc-500" data-testid="lead-next-action">
          {data.next_action ? `Next call ${fmtDateTime(data.next_action.due_at)}` : "No next call set"}
        </p>
      </div>

      {canEdit && (
        <div className="border border-zinc-200 rounded-sm p-3 bg-zinc-50 mb-3" data-testid="lead-call-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">What happened (required)</Label>
              <Select value={outcome} onValueChange={setOutcome}>
                <SelectTrigger className="h-8 text-xs mt-1 rounded-sm bg-white" data-testid="call-outcome"><SelectValue placeholder="Pick the outcome" /></SelectTrigger>
                <SelectContent>
                  {(data.outcomes || []).map(o => <SelectItem key={o.key} value={o.key} data-testid={`call-outcome-${o.key}`}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[10px] uppercase text-zinc-500">
                {closing ? "Next call (not needed — this closes the lead)" : "Next call, date & time (required)"}
              </Label>
              <Input type="datetime-local" value={dueAt} disabled={closing}
                onChange={e => setDueAt(e.target.value)}
                className="h-8 text-xs mt-1 rounded-sm bg-white" data-testid="call-next-at" />
            </div>
          </div>
          <div className="mt-2">
            <Label className="text-[10px] uppercase text-zinc-500">Notes (required — what was said)</Label>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2}
              placeholder="What did the customer say?"
              className="mt-1 text-xs rounded-sm bg-white" data-testid="call-notes" />
          </div>
          {closing && (
            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2" data-testid="call-lost-reason-block">
              <div>
                <Label className="text-[10px] uppercase text-zinc-500">Why is it lost (required)</Label>
                <Select value={lostReason} onValueChange={setLostReason}>
                  <SelectTrigger className="h-8 text-xs mt-1 rounded-sm bg-white" data-testid="call-lost-reason"><SelectValue placeholder="Pick a reason" /></SelectTrigger>
                  <SelectContent>
                    {(data.lost_reasons || []).map(r => <SelectItem key={r} value={r} data-testid={`call-lost-${r}`}>{r}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {lostReason === "Other" && (
                <div>
                  <Label className="text-[10px] uppercase text-zinc-500">Say why</Label>
                  <Input value={lostOther} onChange={e => setLostOther(e.target.value)}
                    className="h-8 text-xs mt-1 rounded-sm bg-white" data-testid="call-lost-other" />
                </div>
              )}
            </div>
          )}
          <div className="mt-2 flex items-center justify-between">
            {dirty ? <p className="text-[10px] text-amber-700" data-testid="call-unsaved-hint">This note is not saved yet.</p> : <span />}
            <Button size="sm" className="h-8 text-xs rounded-sm" disabled={busy} onClick={saveCall} data-testid="call-save-btn">
              {busy ? "Saving…" : "Save call"}
            </Button>
          </div>
        </div>
      )}

      <p className="text-[10px] uppercase tracking-wide text-zinc-500 font-semibold mb-1" data-testid="lead-call-history-title">
        Call history ({calls.length})
      </p>
      <div className="space-y-1 max-h-[420px] overflow-y-auto" data-testid="lead-call-history">
        {calls.map(a => (
          <div key={a._id} className="border border-zinc-100 rounded-sm p-2" data-testid={`lead-call-${a._id}`}>
            <p className="text-[10px] text-zinc-500 font-mono">{fmtDateTime(a.occurred_at)} · {a.owner_name} · {a.outcome}</p>
            <p className="text-xs text-zinc-800 whitespace-pre-wrap">{a.summary}</p>
          </div>
        ))}
        {calls.length === 0 && <p className="text-xs text-zinc-400 py-2" data-testid="lead-no-calls">No call has been logged on this lead yet.</p>}
      </div>

      {/* Fix 6(a) — the in-app "leave anyway?" dialog (Leave / Stay) */}
      {leaveAsk && (
        <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center" data-testid="call-unsaved-dialog">
          <div className="bg-white rounded-sm p-4 w-[400px] border border-zinc-200">
            <p className="text-sm font-semibold text-zinc-900">You have an unsaved call note — leave anyway?</p>
            <p className="text-xs text-zinc-500 mt-1">The note stays as a draft on this lead if you leave.</p>
            <div className="flex gap-2 mt-3 justify-end">
              <Button size="sm" variant="outline" className="rounded-sm h-8 text-xs"
                onClick={() => setLeaveAsk(null)} data-testid="call-unsaved-stay">Stay</Button>
              <Button size="sm" className="rounded-sm h-8 text-xs"
                onClick={() => { const go = leaveAsk; setLeaveAsk(null); go(); }}
                data-testid="call-unsaved-leave">Leave</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
